import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { dbInsert } from '../../../lib/supabase';

const ContactForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(false);

  const { register, handleSubmit, watch, formState: { errors }, reset, control } = useForm();
  const projectType = watch('projectType');

  const projectTypeOptions = [
    { value: 'web-development', label: 'Web Development' },
    { value: 'mobile-app', label: 'Mobile Application' },
    { value: 'digital-transformation', label: 'Digital Transformation' },
    { value: 'ecommerce', label: 'E-commerce Solution' },
    { value: 'custom-software', label: 'Custom Software' },
    { value: 'consultation', label: 'Technical Consultation' }
  ];

  const budgetOptions = [
    { value: 'under-50k', label: 'Under ₹50,000' },
    { value: '50k-1.5L', label: '₹50,000 - ₹1,50,000' },
    { value: '1.5L-3L', label: '₹1,50,000 - ₹3,00,000' },
    { value: '3L-6L', label: '₹3,00,000 - ₹6,00,000' },
    { value: 'above-6L', label: 'Above ₹6,00,000' },
    { value: 'discuss', label: "Let's Discuss" }
  ];

  const timelineOptions = [
    { value: '1-2-months', label: '1-2 Months' },
    { value: '3-4-months', label: '3-4 Months' },
    { value: '5-6-months', label: '5-6 Months' },
    { value: '6-months+', label: '6+ Months' },
    { value: 'flexible', label: 'Flexible Timeline' }
  ];

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    setSubmitError(false);

    try {
      // Save to Supabase / Fallback local storage
      await dbInsert('submissions', {
        ...data,
        status: 'new',
        source: 'contact_form'
      });

      // Construct Gmail Web compose link
      const emailBody = `Full Name: ${data.fullName}\nEmail: ${data.email}\nPhone: ${data.phone || '—'}\nCompany: ${data.company || '—'}\nProject Type: ${data.projectType || '—'}\nBudget Range: ${data.budget || '—'}\nTimeline: ${data.timeline || '—'}\n\nRequirements / Message:\n${data.requirements || '—'}`;
      const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=bytepairs2026@gmail.com&su=${encodeURIComponent(`New Message from ${data.fullName}`)}&body=${encodeURIComponent(emailBody)}`;

      setSubmitSuccess(true);
      
      // Open Gmail Web Compose in a new tab
      window.open(gmailUrl, '_blank');

      reset();

      setTimeout(() => {
        setSubmitSuccess(false);
      }, 8000);
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitSuccess) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-success/10 border border-success/20 rounded-xl p-8 text-center"
      >
        <div className="w-16 h-16 bg-success/20 rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon name="CheckCircle" size={32} color="var(--color-success)" />
        </div>
        <h3 className="text-xl font-semibold text-success mb-2">Message Sent Successfully!</h3>
        <p className="text-text-secondary mb-4">
          Thank you for reaching out. We'll get back to you within 2 hours during business hours.
        </p>
        <p className="text-sm text-text-secondary">
          Check your email for an immediate confirmation with next steps.
        </p>
      </motion.div>
    );
  }

  return (
    <motion.form
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      onSubmit={handleSubmit(onSubmit)}
      className="space-y-6"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Input
          label="Full Name"
          type="text"
          placeholder="John Doe"
          required
          error={errors.fullName?.message}
          {...register('fullName', {
            required: 'Full name is required',
            minLength: { value: 2, message: 'Name must be at least 2 characters' }
          })}
        />

        <Input
          label="Email Address"
          type="email"
          placeholder="john@company.com"
          required
          error={errors.email?.message}
          {...register('email', {
            required: 'Email is required',
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
              message: 'Invalid email address'
            }
          })}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Input
          label="Phone Number"
          type="tel"
          placeholder="+1 (555) 123-4567"
          error={errors.phone?.message}
          {...register('phone', {
            pattern: {
              value: /^[\+]?[1-9][\d]{0,15}$/,
              message: 'Invalid phone number'
            }
          })}
        />

        <Input
          label="Company Name"
          type="text"
          placeholder="Your Company"
          error={errors.company?.message}
          {...register('company')}
        />
      </div>

      <Controller
        name="projectType"
        control={control}
        rules={{ required: 'Please select a project type' }}
        render={({ field }) => (
          <Select
            label="Project Type"
            placeholder="Select project type"
            options={projectTypeOptions}
            required
            error={errors.projectType?.message}
            value={field.value}
            onChange={field.onChange}
          />
        )}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Controller
          name="budget"
          control={control}
          render={({ field }) => (
            <Select
              label="Budget Range"
              placeholder="Select budget range"
              options={budgetOptions}
              error={errors.budget?.message}
              value={field.value}
              onChange={field.onChange}
            />
          )}
        />

        <Controller
          name="timeline"
          control={control}
          render={({ field }) => (
            <Select
              label="Timeline"
              placeholder="Select timeline"
              options={timelineOptions}
              error={errors.timeline?.message}
              value={field.value}
              onChange={field.onChange}
            />
          )}
        />
      </div>

      {projectType && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          transition={{ duration: 0.3 }}
        >
          <Input
            label={`${projectType === 'web-development' ? 'Website' : projectType === 'mobile-app' ? 'App' : 'Project'} Requirements`}
            type="textarea"
            placeholder={`Tell us about your ${projectType === 'web-development' ? 'website' : projectType === 'mobile-app' ? 'mobile app' : 'project'} requirements, features needed, target audience, and any specific goals...`}
            rows={4}
            error={errors.requirements?.message}
            {...register('requirements', {
              minLength: { value: 20, message: 'Please provide more details (minimum 20 characters)' }
            })}
          />
        </motion.div>
      )}

      <div className="flex items-start space-x-3">
        <input
          type="checkbox"
          id="newsletter"
          className="mt-1 w-4 h-4 text-primary bg-background border-border rounded focus:ring-primary focus:ring-2"
          {...register('newsletter')}
        />
        <label htmlFor="newsletter" className="text-sm text-text-secondary">
          I'd like to receive updates about new services, industry insights, and special offers from bytepairs Digital Solutions.
        </label>
      </div>

      <Button
        type="submit"
        variant="default"
        size="lg"
        fullWidth
        loading={isSubmitting}
        iconName="Send"
        iconPosition="right"
        className="bg-gradient-to-r from-primary to-accent hover:from-primary/90 hover:to-accent/90 shadow-brand"
      >
        {isSubmitting ? 'Sending Message...' : 'Send Message'}
      </Button>

      <div className="text-center">
        <p className="text-sm text-text-secondary">
          By submitting this form, you agree to our{' '}
          <span className="text-primary hover:underline cursor-pointer">Privacy Policy</span>
          {' '}and{' '}
          <span className="text-primary hover:underline cursor-pointer">Terms of Service</span>
        </p>
      </div>
    </motion.form>
  );
};

export default ContactForm;